import Footer from "@/components/Footer";
import { usePermissions } from "@/hooks/usePermissions";
import { useTableFiltersCursor } from "@/hooks/useTableFiltersCursor";
import { Box, Button, Card, HStack, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { canChange } from "./changes";
import { CONTRACT_TABLE_COLUMNS } from "./columns";
import ContractDetailModal from "./components/ContractDetailModal";
import Filters from "./components/Filters";
import RenewModal from "./components/RenewModal";
import RetireModal from "./components/RetireModal";
import Table from "./components/Table";
import { Contract, ContractFilters } from "./types";
import { useFetchContracts } from "./useFetchContracts";

const TABLE_KEY = "protectContracts";

type ChangeModal = {
  type: "renew" | "retire";
  contracts: Contract[];
  open: boolean;
};

const ContractsTab = (): JSX.Element => {
  const { isEditor } = usePermissions();
  const { queryFilters, setQueryFilters, cursor, setCursor } =
    useTableFiltersCursor<ContractFilters>(TABLE_KEY);
  const { contracts, nextCursor, isLoading } = useFetchContracts(
    queryFilters,
    cursor
  );
  const [selectedContract, setSelectedContract] = useState<
    Contract | undefined
  >(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [changeModal, setChangeModal] = useState<ChangeModal>();

  useEffect(() => setSelectedIds(new Set()), [queryFilters, cursor]);

  const selectedContracts = contracts.filter((contract) =>
    selectedIds.has(contract.id)
  );

  const handleToggle = (contractId: string, selected: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(contractId);
      else next.delete(contractId);
      return next;
    });

  const handleToggleAll = (selected: boolean) =>
    setSelectedIds(
      new Set(
        selected
          ? contracts.filter(canChange).map((contract) => contract.id)
          : []
      )
    );

  const closeChangeModal = (changed: boolean) => {
    setChangeModal((prev) => prev && { ...prev, open: false });
    if (changed) setSelectedIds(new Set());
  };

  return (
    <Box display="flex" flexDirection="column" gap="1.5rem" flex="1" minH={0}>
      <Card.Root
        borderRadius="lg"
        border="none"
        display="flex"
        flexDirection="column"
        gap="1rem"
        flexShrink={0}
        boxShadow="rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
      >
        <Card.Body p="4">
          <Filters
            queryFilters={queryFilters}
            setQueryFilters={setQueryFilters}
          />
        </Card.Body>
      </Card.Root>
      {isEditor && selectedContracts.length > 0 && (
        <Card.Root
          borderRadius="lg"
          border="none"
          bg="palomma.50"
          flexShrink={0}
        >
          <Card.Body p="3">
            <HStack justify="space-between" gap={4}>
              <Text fontSize="sm" color="palomma.700">
                ✓ {selectedContracts.length} contrato(s) seleccionado(s)
              </Text>
              <HStack gap={2}>
                <Button
                  size="sm"
                  onClick={() =>
                    setChangeModal({
                      type: "renew",
                      contracts: selectedContracts,
                      open: true,
                    })
                  }
                >
                  Renovar / Aumentar canon
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorPalette="red"
                  onClick={() =>
                    setChangeModal({
                      type: "retire",
                      contracts: selectedContracts,
                      open: true,
                    })
                  }
                >
                  Retirar
                </Button>
              </HStack>
            </HStack>
          </Card.Body>
        </Card.Root>
      )}
      <Table
        contracts={contracts}
        isLoading={isLoading}
        onSelect={setSelectedContract}
        selectable={isEditor}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onToggleAll={handleToggleAll}
      />
      <ContractDetailModal
        open={!!selectedContract}
        onClose={() => setSelectedContract(undefined)}
        contract={selectedContract}
      />
      {changeModal?.type === "renew" && (
        <RenewModal
          open={changeModal.open}
          contracts={changeModal.contracts}
          onClose={closeChangeModal}
          onExitComplete={() => setChangeModal(undefined)}
        />
      )}
      {changeModal?.type === "retire" && (
        <RetireModal
          open={changeModal.open}
          contracts={changeModal.contracts}
          onClose={closeChangeModal}
          onExitComplete={() => setChangeModal(undefined)}
        />
      )}
      <Footer
        tableProps={{
          fileName: "contratos",
          items: contracts,
          columns: CONTRACT_TABLE_COLUMNS,
          pagination: { tableKey: TABLE_KEY, nextCursor, setCursor, cursor },
        }}
      />
    </Box>
  );
};

export default ContractsTab;
